import * as Schema from "effect/Schema";
import { ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { PreviewTabId, PreviewRenderedViewportSize } from "./preview.ts";

const Id = TrimmedNonEmptyString.check(Schema.isMaxLength(128));
const Text = Schema.String.check(Schema.isMaxLength(4096));
const Position = Schema.Number.check(Schema.isGreaterThanOrEqualTo(0)).check(
  Schema.isLessThanOrEqualTo(8192),
);
export const CloudBrowserCommand = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("navigate"), url: Text }),
  Schema.Struct({ kind: Schema.Literal("click"), x: Position, y: Position }),
  Schema.Struct({ kind: Schema.Literal("type"), text: Text }),
  Schema.Struct({
    kind: Schema.Literal("press"),
    key: Schema.Literals([
      "Enter",
      "Tab",
      "Backspace",
      "Escape",
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
    ]),
  }),
  Schema.Struct({
    kind: Schema.Literal("scroll"),
    deltaY: Schema.Number.check(Schema.isGreaterThanOrEqualTo(-4096)).check(
      Schema.isLessThanOrEqualTo(4096),
    ),
  }),
  Schema.Struct({ kind: Schema.Literals(["back", "forward", "refresh"]) }),
]);
export const CloudBrowserControlInput = Schema.Struct({
  threadId: ThreadId,
  action: Schema.Union([
    Schema.Struct({ kind: Schema.Literal("inspect") }),
    Schema.Struct({ kind: Schema.Literal("takeControl"), requestId: Id }),
    Schema.Struct({ kind: Schema.Literals(["releaseControl", "close"]), leaseId: Id }),
    Schema.Struct({
      kind: Schema.Literal("input"),
      leaseId: Id,
      sequence: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
      frameId: Id,
      command: CloudBrowserCommand,
    }),
  ]),
});
export type CloudBrowserControlInput = typeof CloudBrowserControlInput.Type;

export const CloudBrowserFrame = Schema.Struct({
  tabId: Schema.NullOr(PreviewTabId),
  frameId: Id,
  browserInstanceId: Id,
  revision: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  url: Schema.NullOr(Schema.String),
  title: Schema.NullOr(Schema.String),
  viewport: PreviewRenderedViewportSize,
  image: Schema.NullOr(Schema.String.check(Schema.isMaxLength(2 * 1024 * 1024))),
  control: Schema.Literals(["cleo", "person"]),
  leaseId: Schema.NullOr(Id),
  nextSequence: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
  focusedField: Schema.NullOr(Schema.Struct({ label: Text, secret: Schema.Boolean })),
});
export type CloudBrowserFrame = typeof CloudBrowserFrame.Type;

export class CloudBrowserControlError extends Schema.TaggedErrorClass<CloudBrowserControlError>()(
  "CloudBrowserControlError",
  { reason: Schema.Literals(["unavailable", "failed"]) },
) {
  override get message() {
    return this.reason === "unavailable"
      ? "The cloud browser is not available on this connection."
      : "The browser could not complete this request. Refresh the view to see what happened.";
  }
}
